import type { ComponentType, ReactNode } from 'react';
import { Building2, CheckCircle2, Eye, Send } from 'lucide-react';
import { SocialPlatform, type LinkedInApiType } from '@mg/shared';

/**
 * What the connect dialog (ConnectModal) says, per platform, and for LinkedIn per connection type. The
 * dialog only explains what happens next: the app keys come from Site Integrations, so it asks for
 * nothing. JSX data, so a bold word keeps the dialog's typography (`font-semibold`).
 */

type IconComponent = ComponentType<{ className?: string }>;

/** One line of "What we'll be able to do": its icon (decorative) and its words. */
export interface ConnectPermission {
  Icon: IconComponent;
  text: ReactNode;
}

/** One connect dialog's copy. */
export interface ConnectCopy {
  title: string;
  /** Beside the platform tile. */
  intro: ReactNode;
  /** "What happens next", in order. */
  steps: readonly [ReactNode, ReactNode, ReactNode];
  /** "What we'll be able to do", in order. */
  permissions: readonly [ConnectPermission, ConnectPermission, ConnectPermission];
  /** A small note under the permissions (Facebook only). */
  permissionsNote: ReactNode | null;
  /** The tinted line under the permissions card. */
  footnote: ReactNode | null;
  /** The primary button. */
  continueLabel: string;
}

/** Said in the dialog, under its buttons, when a start or a reconnect fails, whatever the server said. */
export const START_FAILED = "We couldn't start the connection. Please try again.";

/**
 * Said on the Connections page when an Instagram sign-in through Facebook found no Instagram professional
 * account linked to a Page the person manages (the account picker came back empty).
 */
export const INSTAGRAM_NO_PROFESSIONAL_ACCOUNT =
  "We couldn't find an Instagram professional account linked to a Facebook Page you manage. Connect your Instagram account to a Facebook Page on a Meta Business account, then try again.";

/** Facebook's own page-creation flow: the Facebook dialog's "Create one". */
export const FACEBOOK_CREATE_PAGE_URL = 'https://www.facebook.com/pages/create';

const DISCONNECT_ANYTIME: ConnectPermission = { Icon: CheckCircle2, text: 'You can disconnect anytime.' };

/** The dialogs with one screen each. LinkedIn has one per connection type: {@link LINKEDIN_CONNECT_COPY}. */
export const CONNECT_COPY: Readonly<Partial<Record<SocialPlatform, ConnectCopy>>> = {
  [SocialPlatform.FACEBOOK]: {
    title: 'Connect your Facebook Page',
    intro: (
      <>
        We&apos;ll connect a Facebook <span className="font-semibold">Page</span> (a Business Page) so Smorgas
        Social can keep an eye on its activity and post on your behalf. This is quick, and you can disconnect
        anytime.
      </>
    ),
    steps: [
      "You'll be taken to Facebook to log in.",
      'Choose which Facebook Page to connect.',
      "Approve the permissions and you're done.",
    ],
    permissions: [
      { Icon: Eye, text: "Read your Page's posts & engagement." },
      { Icon: Send, text: 'Publish posts on your behalf.' },
      DISCONNECT_ANYTIME,
    ],
    // An EN DASH (U+2013), as the spec writes it.
    permissionsNote: 'Messages, comments and analytics are optional – turn them on later from the Connections page.',
    footnote: (
      <>
        This connects a Facebook <span className="font-semibold">Page (a Business Page)</span> — not your
        personal profile.
      </>
    ),
    continueLabel: 'Continue to Facebook',
  },
  [SocialPlatform.INSTAGRAM]: {
    title: 'Connect your Instagram account',
    intro: (
      <>
        We&apos;ll connect your <span className="font-semibold">Instagram</span> account through Facebook so
        Smorgas Social can monitor its activity and post on your behalf. This is quick, and you can disconnect
        anytime.
      </>
    ),
    steps: [
      "You'll be taken to Facebook to log in.",
      'Approve the permissions Smorgas Social is requesting.',
      'Choose which linked Instagram account to connect.',
    ],
    permissions: [
      { Icon: Eye, text: 'See your Instagram username and basic profile.' },
      { Icon: Send, text: 'Publish posts on your behalf.' },
      DISCONNECT_ANYTIME,
    ],
    permissionsNote: null,
    // None: the "Before you connect" notice says what the account must be.
    footnote: null,
    continueLabel: 'Continue to Facebook',
  },
  [SocialPlatform.THREADS]: {
    title: 'Connect your Threads account',
    intro: (
      <>
        We&apos;ll connect your <span className="font-semibold">Threads</span> account so Smorgas Social can
        monitor its activity and post on your behalf. This is quick, and you can disconnect anytime.
      </>
    ),
    steps: [
      "You'll be taken to Threads to log in.",
      'Review the permissions Smorgas Social is requesting.',
      "Click Allow and you're connected — no extra steps.",
    ],
    permissions: [
      { Icon: Eye, text: 'See your Threads username and basic profile.' },
      { Icon: Send, text: 'Publish posts on your behalf.' },
      DISCONNECT_ANYTIME,
    ],
    permissionsNote: null,
    footnote: null,
    continueLabel: 'Continue to Threads',
  },
  [SocialPlatform.X]: {
    title: 'Connect your X / Twitter account',
    intro: (
      <>
        We&apos;ll connect your <span className="font-semibold">X / Twitter</span> account so Smorgas Social
        can monitor its activity and post on your behalf. This is quick, and you can disconnect anytime.
      </>
    ),
    steps: [
      "You'll be taken to X / Twitter to log in.",
      'Review the permissions Smorgas Social is requesting.',
      "Click Allow and you're connected — no extra steps.",
    ],
    permissions: [
      { Icon: Eye, text: 'See your X name and username.' },
      { Icon: Send, text: 'Post on your behalf.' },
      DISCONNECT_ANYTIME,
    ],
    permissionsNote: null,
    // None: the OAuth 2.0 set-up note now lives on Site Integrations.
    footnote: null,
    continueLabel: 'Continue to X / Twitter',
  },
};

/** The Instagram dialog's notice, directly under its intro. */
export const INSTAGRAM_BEFORE_YOU_CONNECT = {
  title: 'Before you connect',
  body: (
    <>
      Your Instagram account must be a <span className="font-semibold">professional</span> account connected
      to a <span className="font-semibold">Facebook Page</span> that belongs to a{' '}
      <span className="font-semibold">Meta Business account</span>. Instagram can only be connected by
      logging in with Facebook.
    </>
  ),
};

/** The LinkedIn dialog's copy for one connection type, with the helper under the type selector. */
export interface LinkedInConnectCopy extends ConnectCopy {
  helper: string;
}

/** The LinkedIn connection types, in the order the selector shows them (Community Management first). */
export const LINKEDIN_API_TYPES: readonly LinkedInApiType[] = ['community', 'standard'];

/** The LinkedIn selector: its heading, and each option's title and subtext. */
export const LINKEDIN_SELECTOR_TITLE = 'Which LinkedIn connection do you want?';
export const LINKEDIN_OPTIONS: Readonly<Record<LinkedInApiType, { title: string; subtitle: string }>> = {
  community: { title: 'Community Management', subtitle: 'Connect a Company Page' },
  standard: { title: 'Sign In + Share', subtitle: 'Post as yourself' },
};

// Steps 1 and 2 are the same for both types.
const LINKEDIN_STEP_1 = "You'll be taken to LinkedIn to log in.";
const LINKEDIN_STEP_2 = 'Review the permissions Smorgas Social is requesting.';

/**
 * The LinkedIn dialog per connection type. Only the intro, the helper, step 3, permissions 1 and 2 and
 * the footnote differ; the selected type picks them.
 */
export const LINKEDIN_CONNECT_COPY: Readonly<Record<LinkedInApiType, LinkedInConnectCopy>> = {
  community: {
    title: 'Connect your LinkedIn account',
    intro: (
      <>
        We&apos;ll connect your <span className="font-semibold">LinkedIn</span> so Smorgas Social can keep an
        eye on comments, mentions and analytics, and publish for you. This is quick, and you can disconnect
        anytime.
      </>
    ),
    helper:
      "Connect a Company Page to monitor its comments, mentions and analytics, and post as the Page. You'll pick which Company Page to connect.",
    steps: [LINKEDIN_STEP_1, LINKEDIN_STEP_2, 'Click Allow, then choose which Company Page to connect.'],
    permissions: [
      { Icon: Building2, text: 'Manage your Company Pages and see their analytics, posts, comments and reactions.' },
      { Icon: Send, text: 'Publish posts as the Company Page you choose.' },
      DISCONNECT_ANYTIME,
    ],
    permissionsNote: null,
    footnote: (
      <>
        This connects a LinkedIn <span className="font-semibold">Company Page</span>, and posts go out as that
        Page. To post as yourself, choose Sign In + Share instead.
      </>
    ),
    continueLabel: 'Continue to LinkedIn',
  },
  standard: {
    title: 'Connect your LinkedIn account',
    // What a Sign In + Share connection reads: only /v2/userinfo (name, photo and email), never the feed.
    intro: (
      <>
        We&apos;ll connect your <span className="font-semibold">LinkedIn</span> account so Smorgas Social can
        post as you. It sees only your name, profile photo and email address, not your activity. This is
        quick, and you can disconnect anytime.
      </>
    ),
    helper: 'Connect your personal LinkedIn profile to publish posts as yourself.',
    steps: [LINKEDIN_STEP_1, LINKEDIN_STEP_2, 'Click Allow to finish connecting your profile.'],
    permissions: [
      { Icon: Eye, text: 'See your basic profile, including your name, photo and email address.' },
      { Icon: Send, text: 'Publish posts to LinkedIn as you.' },
      DISCONNECT_ANYTIME,
    ],
    permissionsNote: null,
    footnote: (
      <>
        This connects your personal profile, and posts go out as you. To post as a{' '}
        <span className="font-semibold">Company Page</span>, choose Community Management instead.
      </>
    ),
    continueLabel: 'Continue to LinkedIn',
  },
};
